import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, Space, Tag, Modal, Form, Input, message, Popconfirm, Descriptions, List } from 'antd';

const { Title, Paragraph } = Typography;

const columns = (onEdit, onDelete, onToggleStatus, onDetails, onLogs) => [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
  { title: 'Agency Name', dataIndex: 'name', key: 'name' },
  { title: 'Email', dataIndex: 'email', key: 'email' },
  { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => (
      <Tag color={s === 'active' ? 'green' : 'orange'}>{s}</Tag>
    )
  },
  { title: 'Created', dataIndex: 'created_at', key: 'created_at' },
  {
    title: 'Actions',
    key: 'actions',
    render: (_, record) => (
      <Space>
        <Button size="small" onClick={() => onDetails(record)}>Details</Button>
        <Button size="small" onClick={() => onLogs(record)}>Activity Logs</Button>
        <Button size="small" onClick={() => onEdit(record)}>Edit</Button>
        {record.status === 'active' ? (
          <Popconfirm title="Suspend agency?" onConfirm={() => onToggleStatus(record, 'suspended')} okText="Suspend" okButtonProps={{ danger: true }}>
            <Button size="small" danger>Suspend</Button>
          </Popconfirm>
        ) : (
          <Popconfirm title="Activate agency?" onConfirm={() => onToggleStatus(record, 'active')} okText="Activate">
            <Button size="small" type="primary">Activate</Button>
          </Popconfirm>
        )}
        <Popconfirm title="Delete agency?" onConfirm={() => onDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
          <Button size="small" danger>Delete</Button>
        </Popconfirm>
      </Space>
    )
  }
];

const AllAgenciesList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null=create, object=edit
  const [form] = Form.useForm();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const authHeaders = (base = {}) => {
    const token = sessionStorage.getItem('token');
    return token ? { ...base, Authorization: `Bearer ${token}` } : base;
  };

  const openDetails = async (record) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsData(null);
    try {
      const res = await fetch(`/api/agencies/${record.id}`);
      if (!res.ok) throw new Error(`Failed to load details: ${res.status}`);
      const json = await res.json();
      setDetailsData(json);
    } catch (e) {
      message.error(e.message || 'Failed to load details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const openLogs = async (record) => {
    setLogsOpen(true);
    setLogsLoading(true);
    setLogs([]);
    try {
      const qs = new URLSearchParams({ type: 'agency', message_ilike: String(record.id) });
      const res = await fetch(`/api/logs?${qs.toString()}`);
      if (!res.ok) throw new Error(`Failed to load logs: ${res.status}`);
      const json = await res.json();
      setLogs(Array.isArray(json.items) ? json.items : []);
    } catch (e) {
      message.error(e.message || 'Failed to load logs');
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const res = await fetch(`/api/users?limit=5000&offset=0`);
        if (!res.ok) {
          console.warn('Agencies fetch failed', res.status, res.statusText);
          if (mounted) setData([]);
          return;
        }
        const json = await res.json();
        const items = Array.isArray(json.items) ? json.items : [];
        
        // Filter only enterprises (role = 'agency')
        const enterprisesData = items.filter(user => user.role === 'agency');
        
        const mapped = enterprisesData.map((row) => ({
          id: row.id,
          name: row.full_name || `Enterprise #${row.id}`,
          email: row.email || '', // Now includes email from users table
          status: 'active', // Default for now, can be extended later
          created_at: row.created_at ? new Date(row.created_at).toLocaleDateString() : '',
          owner_user_id: row.id, // The enterprise user is the owner
        }));
        if (mounted) setData(mapped);
      } catch (e) {
        console.warn('Agencies load error:', e);
        if (mounted) setData([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({ name: '', owner_user_id: '' });
    setModalOpen(true);
  };

  const handleToggleStatus = async (record, nextStatus) => {
    try {
      const res = await fetch(`/api/users/${record.id}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error(`Status update failed: ${res.status} ${res.statusText}`);
      await res.json();
      setData(prev => prev.map(r => r.id === record.id ? { ...r, status: nextStatus } : r));
      message.success(`Enterprise ${nextStatus === 'active' ? 'activated' : 'suspended'}`);
    } catch (e) {
      message.error(e.message || 'Failed to update status');
    }
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ name: record.name, owner_user_id: record.owner_user_id ?? '' });
    setModalOpen(true);
  };

  const handleDelete = async (record) => {
    try {
      const res = await fetch(`/api/users/${record.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error(`Delete failed: ${res.status} ${res.statusText}`);
      message.success('Enterprise deleted');
      setData(prev => prev.filter(r => r.id !== record.id));
    } catch (e) {
      message.error(e.message || 'Failed to delete enterprise');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        // Update
        const res = await fetch(`/api/agencies/${editing.id}`, {
          method: 'PUT',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ name: values.name, owner_user_id: values.owner_user_id ? Number(values.owner_user_id) : null }),
        });
        if (!res.ok) throw new Error(`Update failed: ${res.status} ${res.statusText}`);
        const updated = await res.json();
        setData(prev => prev.map(r => r.id === editing.id ? {
          ...r,
          name: updated.name,
          owner_user_id: updated.owner_user_id,
        } : r));
        message.success('Agency updated');
      } else {
        // Create
        const res = await fetch(`/api/agencies`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ name: values.name, owner_user_id: values.owner_user_id ? Number(values.owner_user_id) : null }),
        });
        if (!res.ok) throw new Error(`Create failed: ${res.status} ${res.statusText}`);
        const created = await res.json();
        setData(prev => [{
          id: created.id,
          name: created.name,
          owner_user_id: created.owner_user_id,
          email: '',
          status: created.status || 'active',
          created_at: created.created_at ? new Date(created.created_at).toLocaleDateString() : '',
        }, ...prev]);
        message.success('Agency created');
      }
      setModalOpen(false);
    } catch (e) {
      if (e?.errorFields) return; // form errors
      message.error(e.message || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Agencies' },
        { title: 'Enterprise Agencies' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Enterprise Agencies</Title>
        <Space>
          <Button type="primary" onClick={openCreate}>New Agency</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Loaded from <code>/api/agencies</code>. Email/Status are placeholders unless added to the API.
        </Paragraph>
        <Table
          rowKey="id"
          columns={columns(openEdit, handleDelete, handleToggleStatus, openDetails, openLogs)}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        open={modalOpen}
        title={editing ? `Edit Agency #${editing.id}` : 'New Agency'}
        onOk={onSubmit}
        okButtonProps={{ loading: saving }}
        onCancel={() => setModalOpen(false)}
      >
        <Form layout="vertical" form={form}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="Agency name" />
          </Form.Item>
          <Form.Item label="Owner User ID" name="owner_user_id">
            <Input placeholder="Owner user id (optional)" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={detailsOpen}
        title="Agency Details"
        footer={null}
        onCancel={() => setDetailsOpen(false)}
      >
        {detailsLoading ? (
          <Paragraph>Loading...</Paragraph>
        ) : detailsData ? (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="ID">{detailsData.id}</Descriptions.Item>
            <Descriptions.Item label="Name">{detailsData.name}</Descriptions.Item>
            <Descriptions.Item label="Owner User ID">{detailsData.owner_user_id ?? ''}</Descriptions.Item>
            <Descriptions.Item label="Status">{detailsData.status}</Descriptions.Item>
            <Descriptions.Item label="Created At">{detailsData.created_at ? new Date(detailsData.created_at).toLocaleString() : ''}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Paragraph type="secondary">No details.</Paragraph>
        )}
      </Modal>

      <Modal
        open={logsOpen}
        title="Agency Activity Logs"
        footer={null}
        onCancel={() => setLogsOpen(false)}
        width={720}
      >
        {logsLoading ? (
          <Paragraph>Loading...</Paragraph>
        ) : (
          <List
            dataSource={logs}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  title={`${new Date(item.timestamp).toLocaleString()} — ${item.type}`}
                  description={item.message}
                />
                <div>User #{item.user_id ?? ''}</div>
              </List.Item>
            )}
          />
        )}
      </Modal>
    </div>
  );
};

export default AllAgenciesList;
